import React, { useMemo, useState } from "react";
import styles from "./CalibrationRequestForm.module.scss";
import InfoTooltip from "./InfoTooltip";
import dragonflySerialImg from "../../pics/request-calibration/numero_serie_dragonFly.jpeg";
import boneConductionSerialImg from "../../pics/request-calibration/numero_serie_conduction_osseuse.jpeg";
import dragonflyFamilyImg from "../../pics/request-calibration/dragonfly-family.jpg";
import artificialEarImg from "../../pics/request-calibration/oreilles-artificielle.jpg";

const normalizeSearchValue = (value) => String(value || "").toLowerCase().trim();

const CabineField = ({ t, cabine, formData, onChange }) => {
  const [cabineSearch, setCabineSearch] = useState("");
  const [isCabineSelectOpen, setIsCabineSelectOpen] = useState(false);
  const searchValue = normalizeSearchValue(cabineSearch);

  const authorizedCabines = useMemo(
    () => (Array.isArray(cabine) ? cabine : []),
    [cabine]
  );

  const displayedCabines = useMemo(() => {
    const filteredCabines = searchValue
      ? authorizedCabines.filter((c) => {
          const cabName = normalizeSearchValue(c.cabname || `Cabine ${c.id}`);
          const cabId = normalizeSearchValue(c.id);

          return cabName.includes(searchValue) || cabId.includes(searchValue);
        })
      : authorizedCabines;

    const selectedCabine = authorizedCabines.find(
      (c) => String(c.id) === String(formData.cabineId)
    );

    if (
      selectedCabine &&
      !filteredCabines.some((c) => String(c.id) === String(selectedCabine.id))
    ) {
      return [selectedCabine, ...filteredCabines];
    }

    return filteredCabines;
  }, [authorizedCabines, formData.cabineId, searchValue]);

  return (
    <label className={styles.field}>
      <span>{t("calibration_request.field_cabine")}</span>
      <input
        type="search"
        value={cabineSearch}
        onFocus={() => setIsCabineSelectOpen(true)}
        onChange={(e) => {
          setCabineSearch(e.target.value);
          setIsCabineSelectOpen(true);
        }}
        placeholder={t("calibration_request.search_cabine")}
      />
      <select
        name="cabineId"
        value={formData.cabineId}
        onFocus={() => setIsCabineSelectOpen(true)}
        onBlur={() => setIsCabineSelectOpen(false)}
        onChange={(e) => {
          onChange(e);
          setIsCabineSelectOpen(false);
        }}
        size={
          isCabineSelectOpen
            ? Math.min(Math.max(displayedCabines.length + 1, 2), 8)
            : undefined
        }
        required
      >
        <option value="">{t("calibration_request.select_cabine")}</option>
        {displayedCabines.length > 0 ? (
          displayedCabines.map((c) => (
            <option key={c.id} value={c.id}>
              {c.cabname || `Cabine ${c.id}`}
            </option>
          ))
        ) : (
          <option value="" disabled>
            {t("calibration_request.no_cabine_found")}
          </option>
        )}
      </select>
    </label>
  );
};

const SerialField = ({
  t,
  labelKey,
  infoKey,
  imageSrc,
  inputName,
  inputValue,
  checkboxName,
  checkboxChecked,
  checkboxLabelKey,
  placeholderKey,
  onChange,
}) => (
  <div className={styles.serialField}>
    <div className={styles.serialHeader}>
      <span>{t(labelKey)}</span>
      <InfoTooltip text={t(infoKey)} imageSrc={imageSrc} />
    </div>
    <input
      type="text"
      name={inputName}
      value={inputValue}
      onChange={onChange}
      disabled={checkboxChecked}
      placeholder={t(placeholderKey)}
      required={!checkboxChecked}
    />
    <label className={styles.checkbox}>
      <input
        type="checkbox"
        name={checkboxName}
        checked={checkboxChecked}
        onChange={onChange}
      />
      <span>{t(checkboxLabelKey)}</span>
    </label>
  </div>
);

const CalibrationRequestDetailsForm = ({
  t,
  cabine,
  formData,
  formTitleKey,
  formDescKey,
  addressLoading,
  isBone,
  isEars,
  isHeadphonesCalibration,
  isInsertsCalibration,
  onChange,
  onSubmit,
}) => (
  <div className={styles.formLayout}>
    {isBone && (
      <div className={styles.warningTopRight}>
        <div className={styles.warningBox}>
          <span>{t("calibration_request.bone_warning")}</span>
          <InfoTooltip imageSrc={dragonflyFamilyImg} />
        </div>
      </div>
    )}

    <div className={styles.headerInfo}>
      <h4>{t(formTitleKey)}</h4>
      <p>{t(formDescKey)}</p>
      {(isHeadphonesCalibration || isInsertsCalibration) && (
        <img
          className={styles.headerImage}
          src={artificialEarImg}
          alt={t("calibration_request.artificial_ear_image_alt")}
        />
      )}
      {isEars && <p>{t("calibration_request.ears_tnt_info")}</p>}
    </div>

    <form onSubmit={onSubmit} className={styles.form}>
      <CabineField
        t={t}
        cabine={cabine}
        formData={formData}
        onChange={onChange}
      />

      <label className={styles.field}>
        <span>{t("calibration_request.field_name")}</span>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          required
        />
      </label>

      <label className={styles.field}>
        <span>{t("calibration_request.field_email")}</span>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onChange}
          required
        />
      </label>

      <label className={styles.field}>
        <span>{t("calibration_request.field_phone")}</span>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={onChange}
          placeholder={t("calibration_request.phone_placeholder")}
        />
      </label>

      <fieldset className={styles.addressGroup}>
        <legend>{t("calibration_request.field_address_title")}</legend>
        {addressLoading && (
          <p className={styles.addressLoading}>
            {t("calibration_request.loading_address")}
          </p>
        )}
        <label className={styles.field}>
          <span>{t("calibration_request.field_center_name")}</span>
          <input
            type="text"
            name="centerName"
            value={formData.centerName}
            onChange={onChange}
            required
          />
        </label>
        <label className={styles.field}>
          <span>{t("calibration_request.field_address")}</span>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={onChange}
            required
          />
        </label>
        <label className={styles.field}>
          <span>{t("calibration_request.field_address2")}</span>
          <input
            type="text"
            name="address2"
            value={formData.address2}
            onChange={onChange}
          />
        </label>
        <div className={styles.addressRow}>
          <label className={styles.field}>
            <span>{t("calibration_request.field_zipcode")}</span>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={onChange}
              required
            />
          </label>
          <label className={styles.field}>
            <span>{t("calibration_request.field_city")}</span>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={onChange}
              required
            />
          </label>
        </div>
        <label className={styles.field}>
          <span>{t("calibration_request.field_country")}</span>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={onChange}
          />
        </label>
      </fieldset>

      {isBone && (
        <>
          <SerialField
            t={t}
            labelKey="calibration_request.field_serial_dragonfly"
            infoKey="calibration_request.info_dragonfly"
            imageSrc={dragonflySerialImg}
            inputName="serialDragonfly"
            inputValue={formData.serialDragonfly}
            checkboxName="noDragonfly"
            checkboxChecked={formData.noDragonfly}
            checkboxLabelKey="calibration_request.no_serial"
            placeholderKey="calibration_request.serial_placeholder"
            onChange={onChange}
          />

          <SerialField
            t={t}
            labelKey="calibration_request.field_serial_co"
            infoKey="calibration_request.info_co_serial"
            imageSrc={boneConductionSerialImg}
            inputName="serialCO"
            inputValue={formData.serialCO}
            checkboxName="noCO"
            checkboxChecked={formData.noCO}
            checkboxLabelKey="calibration_request.no_serial_co"
            placeholderKey="calibration_request.serial_placeholder"
            onChange={onChange}
          />
        </>
      )}

      <label className={styles.field}>
        <span>{t("calibration_request.field_message")}</span>
        <textarea
          name="message"
          value={formData.message}
          onChange={onChange}
          rows={3}
          placeholder={t("calibration_request.message_placeholder")}
        />
      </label>

      <button type="submit" className={styles.submitBtn}>
        {t("calibration_request.next_step")}
      </button>
    </form>
  </div>
);

export default CalibrationRequestDetailsForm;
